/** Money, distance and time as the rider sees them. Money is integer paise; times are shown in India time. */
import { prettyPhone } from './phone';
const IST = 'Asia/Kolkata';

export function rupees(paise: number): string {
  const r = Math.round(paise / 100);
  return `₹${r.toLocaleString('en-IN')}`;
}

/** "450 m", "7.1 km", "14 km" — one decimal under 10 km. */
export function km(meters: number, decimals?: number): string {
  if (meters < 1000) return `${Math.max(10, Math.round(meters / 10) * 10)} m`;
  const k = meters / 1000;
  const d = decimals ?? (k < 10 ? 1 : 0);
  return `${k.toFixed(d).replace(/\.0$/, '')} km`;
}

export const minutes = (seconds: number) => Math.max(1, Math.round(seconds / 60));

function parts(d: Date) {
  const f = new Intl.DateTimeFormat('en-IN', { timeZone: IST, hour: 'numeric', minute: '2-digit', hour12: true });
  const p = Object.fromEntries(f.formatToParts(d).map((x) => [x.type, x.value]));
  return { h: p.hour, m: p.minute, ap: (p.dayPeriod ?? '').toLowerCase() };
}

/** "6:43 pm" */
export function clock(iso: string | Date): string {
  const { h, m, ap } = parts(new Date(iso));
  return `${h}:${m} ${ap}`;
}

/** ["6:43", "pm"] */
export function clockParts(iso: string | Date): [string, string] {
  const { h, m, ap } = parts(new Date(iso));
  return [`${h}:${m}`, ap];
}

const dayKey = (d: Date) => new Intl.DateTimeFormat('en-CA', { timeZone: IST }).format(d);

/** "TODAY", "YESTERDAY" or "18 OCT" (India calendar days). */
export function dayLabel(iso: string, now = new Date()): string {
  const d = new Date(iso);
  if (dayKey(d) === dayKey(now)) return 'TODAY';
  if (dayKey(d) === dayKey(new Date(now.getTime() - 86_400_000))) return 'YESTERDAY';
  return new Intl.DateTimeFormat('en-IN', { timeZone: IST, day: 'numeric', month: 'short' }).format(d).toUpperCase();
}

/** "Last Sunday", "Yesterday", "Today", or "12 Oct". */
export function relativeDay(iso: string, now = new Date()): string {
  const d = new Date(iso);
  const days = Math.round((Date.parse(dayKey(now)) - Date.parse(dayKey(d))) / 86_400_000);
  if (days <= 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 7) return `Last ${new Intl.DateTimeFormat('en-IN', { timeZone: IST, weekday: 'long' }).format(d)}`;
  return new Intl.DateTimeFormat('en-IN', { timeZone: IST, day: 'numeric', month: 'short' }).format(d);
}

/** "March" — month name for "since March". */
export const monthName = (iso: string) => new Intl.DateTimeFormat('en-IN', { timeZone: IST, month: 'long' }).format(new Date(iso));

/** India-time hour 0–23. */
export function istHour(now = new Date()): number {
  return Number(new Intl.DateTimeFormat('en-GB', { timeZone: IST, hour: 'numeric', hour12: false }).format(now)) % 24;
}

/** Greeting by India time of day, in the rider's language: "Shaam ho gayi, Arjun" (Hinglish). */
export function greetingKey(now = new Date()): 'greet.morning' | 'greet.noon' | 'greet.evening' | 'greet.night' {
  const h = istHour(now);
  return h >= 4 && h < 12 ? 'greet.morning' : h >= 12 && h < 16 ? 'greet.noon' : h >= 16 && h < 20 ? 'greet.evening' : 'greet.night';
}
export function greeting(t: (k: ReturnType<typeof greetingKey>) => string, name: string | null | undefined, now = new Date()): string {
  const part = t(greetingKey(now));
  const first = (name ?? '').trim().split(/\s+/)[0];
  return first ? `${part}, ${first}` : part;
}

/** After dark in Jourian (India time): 7 pm to 6 am. Used for automatic trip sharing. */
export const isAfterDark = (now = new Date()) => {
  const h = istHour(now);
  return h >= 19 || h < 6;
};

/** "+91 98765 43221" from "+919876543221" or "9876543221". */
export function phoneDisplay(p: string): string {
  // any country, in its international form ("+44 7400 123456"); a bare 10-digit number is an Indian one
  if (p.startsWith('+') && !p.startsWith('+91')) return prettyPhone(p);
  const d = p.replace(/\D/g, '').slice(-10);
  return d.length === 10 ? `+91 ${d.slice(0, 5)} ${d.slice(5)}` : p;
}

/** Indian mobile from whatever was typed: 10 digits starting 6–9, or null. */
export function normaliseMobile(input: string): string | null {
  let d = input.replace(/\D/g, '');
  if (d.length === 12 && d.startsWith('91')) d = d.slice(2);
  if (d.length === 11 && d.startsWith('0')) d = d.slice(1);
  return /^[6-9]\d{9}$/.test(d) ? d : null;
}

/** "98765 43221" as the rider types. */
export function groupMobile(digits: string): string {
  const d = digits.replace(/\D/g, '').slice(0, 10);
  return d.length > 5 ? `${d.slice(0, 5)} ${d.slice(5)}` : d;
}

/** "AS" from "Arjun Singh". */
export function initials(name: string | null | undefined): string {
  const w = (name ?? '').trim().split(/\s+/).filter(Boolean);
  if (!w.length) return '';
  return ((w[0][0] ?? '') + (w.length > 1 ? w[w.length - 1][0] : '')).toUpperCase();
}

/** Plate as printed in the mockup: "JK02 AB 4417" from "JK02AB4417". */
export function plateText(plate: string): string {
  const p = plate.replace(/[\s-]/g, '').toUpperCase();
  const m = /^([A-Z]{2}\d{1,2})([A-Z]{0,3})(\d{1,4})$/.exec(p);
  if (m) return [m[1], m[2], m[3]].filter(Boolean).join(' ');
  const bh = /^(\d{2}BH)(\d{4})([A-Z]{1,2})$/.exec(p);
  return bh ? `${bh[1]} ${bh[2]} ${bh[3]}` : plate;
}
